// RD-4141: when a flight looked up earlier may be shown again instead of asking again.
//
// The booking pages kept the flight in localStorage and reused it whenever the flight number and
// date matched, with no age limit — so a flight that was delayed, had departed or had landed since
// the last visit showed its old status and estimate every time the booking was reopened. The
// standalone VIP page and the partner page held the same kind of copy for the life of the page.
//
// A stored lookup is now reused only while it is young enough for how fast the flight can change:
//   - within 48 h of the flight: 2 minutes (the server itself caches a live flight for 60 s);
//   - further out: 30 minutes (a scheduled flight's estimate moves slowly);
//   - a flight that reached its final state (landed, cancelled, diverted, returned): for good.
// A copy stored without a timestamp (before this rule existed) is asked for again once.
//
// Pure apart from the two small localStorage helpers at the bottom.

export const FLIGHT_CACHE_NEAR_MS = 2 * 60 * 1000
export const FLIGHT_CACHE_FAR_MS = 30 * 60 * 1000
export const FLIGHT_CACHE_NEAR_WINDOW_MS = 48 * 60 * 60 * 1000

const RESOLVED = /^(arrived|landed|cancel+ed|diverted|returned)$/i

export const isFlightCacheFresh = (flights, cachedAt, now = Date.now()) => {
  if (!Array.isArray(flights) || !flights.length) return false
  const at = Number(cachedAt)
  if (!Number.isFinite(at) || at <= 0) return false

  const statusOf = f =>
    String((f && f.flightInfo && f.flightInfo.flight_status) || '').trim()
  if (flights.every(f => RESOLVED.test(statusOf(f)))) return true

  // Flight times are the airport's wall clock written as UTC, so against `now` they are off by
  // the time zone (at most 14 h) — small next to a 48 h window.
  const gaps = flights
    .map(f => Date.parse(f && f.time && (f.time.departure || f.time.arrival)))
    .filter(Number.isFinite)
    .map(t => Math.abs(t - now))
  const nearest = gaps.length ? Math.min(...gaps) : 0
  const maxAge =
    nearest <= FLIGHT_CACHE_NEAR_WINDOW_MS
      ? FLIGHT_CACHE_NEAR_MS
      : FLIGHT_CACHE_FAR_MS
  return now - at <= maxAge
}

const AT = '-at'

// Next to `key` (e.g. `${bookingId}-pickUp`), the time it was stored.
export const stampFlightCache = key => {
  try {
    localStorage.setItem(key + AT, String(Date.now()))
  } catch (e) {
    // storage unavailable: the cache just never counts as fresh
  }
}

export const isStoredFlightCacheFresh = (key, flights) => {
  try {
    return isFlightCacheFresh(flights, localStorage.getItem(key + AT))
  } catch (e) {
    return false
  }
}
